// services/auth-v2.service.ts
// Client-side functions for Auth V2 endpoints (passkey + magic link)

import type {
  V2RegisterOptionsResponse,
  V2RegisterVerifyResponse,
  V2LoginOptionsResponse,
  V2LoginVerifyResponse,
  V2MagicLinkSendResponse,
  V2MagicLinkVerifyResponse,
} from "@/utils/types";
import type { RegistrationResponseJSON, AuthenticationResponseJSON } from "@simplewebauthn/browser";

const JSON_HEADERS = { "Content-Type": "application/json" };

async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data: unknown = await res.json();
  if (!res.ok) {
    throw new Error((data as { error?: string }).error ?? `Request failed (${String(res.status)})`);
  }
  return data as T;
}

// ---------- Passkey Registration ----------

export function requestRegisterOptions(email?: string): Promise<V2RegisterOptionsResponse> {
  return post("/api/auth/v2/passkey/register/options", email ? { email } : {});
}

export function verifyRegistration(body: {
  challengeId: string;
  attestation: RegistrationResponseJSON;
  userHandle: string;
  email?: string;
}): Promise<V2RegisterVerifyResponse> {
  return post("/api/auth/v2/passkey/register/verify", body);
}

// ---------- Passkey Login ----------

export function requestLoginOptions(): Promise<V2LoginOptionsResponse> {
  return post("/api/auth/v2/passkey/login/options");
}

export function verifyLogin(body: {
  challengeId: string;
  assertion: AuthenticationResponseJSON;
}): Promise<V2LoginVerifyResponse> {
  return post("/api/auth/v2/passkey/login/verify", body);
}

// ---------- Magic Link ----------

export function sendMagicLink(email: string): Promise<V2MagicLinkSendResponse> {
  return post("/api/auth/v2/magic-link/send", { email });
}

export function verifyMagicLink(token: string): Promise<V2MagicLinkVerifyResponse> {
  return post("/api/auth/v2/magic-link/verify", { token });
}
